import {FilterTastingHall} from 'modules/events/components/FilterTastingHall';
import {FC, useContext} from 'react';
import {EventsListContext} from 'modules/events/context';
import {EventFilter} from 'modules/events/components/EventFilter';
import style from 'modules/events/components/EventFilterList/style.module.scss';
import {EventFilterCodes, EVENTS_FILTERS_CONFIG} from 'modules/events/constants';
import EventFilterPopup from 'modules/events/components/EventFilter/components/EventFilterPopup';
import {useRouter} from 'next/router';
import {TEventsFilterValue} from 'modules/events/types';
import {parseEventsFiltersFromUrl} from 'modules/events/helpers/filters';
import {WineSamplesLink} from 'modules/events/components/WineSamples/WineSamplesLink';

const getSelectedLabels = (
    selectedValues: string[],
    filterValues: TEventsFilterValue[],
    name: EventFilterCodes,
): string[] => {
    return selectedValues
        .map((value) => {
            const filterValue = filterValues.find((fv) => {
                if ('code' in fv) {
                    if (name === EventFilterCodes.price) {
                        return fv.code === Number(value);
                    }
                    return fv.code === value;
                }
                if ('id' in fv) {
                    return fv.id === value;
                }
                return false;
            });

            if (!filterValue) {
                return null;
            }

            if (filterValue && filterValue.title) {
                if (name === EventFilterCodes.price) {
                    return `${filterValue.title} ₽`;
                }
                return filterValue.title;
            }
            return value;
        })
        .filter(Boolean) as string[];
};

export const EventFilterList: FC = () => {
    const {filter, bannerToWineTesting} = useContext(EventsListContext);

    const router = useRouter();
    const currentFiltersFromUrl = parseEventsFiltersFromUrl(router.asPath);

    const renderFilter = ({code, filterValues}: {code: EventFilterCodes; filterValues: TEventsFilterValue[]}) => {
        if (!Array.isArray(filterValues) && code === EventFilterCodes.hall) {
            return <FilterTastingHall key={code} />;
        }

        const configFilter = EVENTS_FILTERS_CONFIG.find((eventFilter) => eventFilter.code === code);

        if (!configFilter?.title) {
            return null;
        }

        const currentFilter = currentFiltersFromUrl.find((f) => f.code === code);
        const selectedValues = currentFilter?.values || [];
        const selectedLabels = getSelectedLabels(selectedValues, filterValues, code);

        const normalizedFilterValues = filterValues.filter((filterValue) => 0 !== filterValue.count);

        return (
            <EventFilter
                key={code}
                title={configFilter.title}
                code={code}
                selectedLabels={selectedLabels}
                isDisabled={0 === normalizedFilterValues.length}
            >
                <EventFilterPopup
                    filterTitle={configFilter.title}
                    filterJoinType={configFilter.valuesJoinType}
                    withSearch={configFilter.isSearchable}
                    filterCode={code}
                    filterValues={normalizedFilterValues}
                />
            </EventFilter>
        );
    };

    const filterEntries = Object.entries(filter).map(([code, values]) => ({
        code: code as EventFilterCodes,
        filterValues: values as TEventsFilterValue[],
    }));

    return (
        <div className={style.root}>
            <div className={style.eventFilters}>
                <div className={style.eventFilterList}>{filterEntries.map(renderFilter)}</div>
                {bannerToWineTesting?.isActive && bannerToWineTesting?.url && (
                    <div className={style.wineSamples}>
                        <WineSamplesLink href={bannerToWineTesting.url} />
                    </div>
                )}
            </div>
        </div>
    );
};
